import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@erp/ui';
import { Search, ChevronDown, Check, X } from '@/components/icons';

export interface SearchableSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  label,
  className,
  disabled,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!search) return options.slice(0, 100);
    const s = search.toLowerCase();
    return options
      .filter(
        (o) =>
          o.label.toLowerCase().includes(s) ||
          o.sublabel?.toLowerCase().includes(s),
      )
      .slice(0, 100);
  }, [options, search]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setSearch('');
    return undefined;
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          data-slot="select-trigger"
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate flex items-center gap-1.5 min-w-0">
            {label && (
              <span className="text-muted-foreground shrink-0">{label}:</span>
            )}
            {selected ? (
              <>
                {selected.sublabel && (
                  <span className="text-muted-foreground">{selected.sublabel}</span>
                )}
                <span className="truncate">{selected.label}</span>
              </>
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {value && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange('');
                  setSearch('');
                }}
                className="inline-flex size-4 items-center justify-center rounded text-muted-foreground hover:text-foreground"
              >
                <X size={12} />
              </span>
            )}
            <ChevronDown size={14} className="text-muted-foreground" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0 z-[200]" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search
              size={14}
              className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              style={{ insetInlineStart: '0.6em' }}
            />
            <Input
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-8"
            />
          </div>
        </div>
        <div
          className="max-h-56 overflow-y-auto"
          onWheel={(e) => {
            // Radix Dialog's react-remove-scroll cancels wheel on portalled
            // popover content. Scroll manually so the list can be wheeled.
            const el = e.currentTarget;
            const atTop = el.scrollTop === 0 && e.deltaY < 0;
            const atBottom =
              Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight &&
              e.deltaY > 0;
            if (!atTop && !atBottom) {
              el.scrollTop += e.deltaY;
              e.stopPropagation();
            }
          }}
        >
          <div className="p-1">
            {filtered.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-4">
                Aucun résultat
              </p>
            ) : (
              filtered.map((option) => {
                const isActive = option.value === value;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full justify-start gap-2 font-normal',
                      isActive && 'bg-accent',
                    )}
                    data-slot="select-item"
                  >
                    <Check
                      size={14}
                      className={cn(
                        'shrink-0',
                        isActive ? 'text-primary' : 'opacity-0',
                      )}
                    />
                    {option.sublabel && (
                      <span className="opacity-60">{option.sublabel}</span>
                    )}
                    <span className="truncate">{option.label}</span>
                  </Button>
                );
              })
            )}
            {filtered.length === 100 && (
              <p className="text-center text-xs text-muted-foreground py-2">
                {search ? 'Affinez votre recherche...' : 'Tapez pour rechercher...'}
              </p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
